import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ExternalLink, AlertTriangle, AlertCircle, CheckCircle2, Clock, Wallet, Target, Sparkles, FileText, Users, ChevronDown } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/dashboard/$id")({
  head: ({ params }) => ({
    meta: [{ title: "Vue dashboard — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Vue dashboard — Hōjin",
        description: "Vue dashboard — Hōjin",
        url: `https://hojin.ai/dashboard/${params.id}`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/dashboard/${params.id}` }],
  }),
  component: DashboardPage,
});

// ─── Types souples (on lit le payload brut) ──────────────────────────────
type AnyObj = Record<string, unknown>;
interface ScoreObj { value?: number; scale?: number; symbol?: string; color_hex?: string }
interface VerdictObj { score?: ScoreObj; phrase?: string; verdict_type?: string }
interface RecoOwner { cadre?: string; raison?: string }
interface Reco {
  id?: string;
  titre?: string;
  pourquoi?: string;
  action_concrete?: string;
  ecueils?: string;
  cout_estime?: string;
  temps_estime?: string;
  priority?: string;
  owner?: RecoOwner;
  lien_ressource?: string | null;
  lien_plateforme_matching?: { url?: string; label?: string } | null;
}
interface MEG { id?: string; titre?: string; explication?: string; action_preventive?: string; severity?: string; lien?: string | null }
interface PlanItem { titre?: string; cout?: string; temps?: string; lien?: string | null }
interface Ressource { titre?: string; type?: string; description?: string; url?: string }
interface Confidence { level?: string; score?: number | null }
interface Metadata { score_confiance_global?: number | null; drapeaux_attention?: string[]; model?: string }
interface Instrument { kind?: string; classification?: string; reco?: string; justification?: string }
interface Observation {
  angle?: string;
  ancrage?: string;
  constat?: string;
  question?: string;
  convergence?: string;
  droit_general?: string;
  pro_a_consulter?: string;
}
interface VerifClarity {
  kind: "verif_observations";
  observations?: Observation[];
  rappel_faits?: string | null;
  angles_non_couverts?: string[];
}
interface StdClarity {
  kind?: string;
  verdict?: VerdictObj;
  phrase?: string;
  pourquoi?: { summary?: string; consensus_points?: string[]; consensus_evidence?: AnyObj[] };
  recommandations?: Reco[];
  mises_en_garde?: MEG[];
  plan_attaque?: { cette_semaine?: PlanItem[]; ce_mois?: PlanItem[]; mois_prochain?: PlanItem[] };
  ressources?: Ressource[];
  confidence?: Confidence;
  metadata?: Metadata;
  instrument?: Instrument;
}
type Clarity = VerifClarity | StdClarity;

interface ActBubble { ai_name?: string; content?: string; round?: number; is_contestation?: boolean }
interface SourceItem { category?: string; title?: string; url?: string; relevance_score?: number | null }

// `items` peut être soit un objet { catégorie: [sources…] } (format actuel
// research_sources_full), soit un tableau plat (legacy). On normalise.
type SourceItemsShape = Record<string, SourceItem[]> | SourceItem[];
interface VerdictPayload {
  conversation_id?: string;
  branch?: string;
  audit_axis?: string | null;
  verdict_clarity?: Clarity | null;
  verdict_audit_diagnostic?: AuditDiagnostic | null;
  acts?: ActBubble[];
  research_sources_full?: { items?: SourceItemsShape; by_category?: Record<string, number>; total?: number };
  research_sources?: { sample?: SourceItem[]; by_category?: Record<string, number>; total?: number };
}

// ─── Audit diagnostic (mode Positionnement) — schéma ─────────────────────
type Feu = "vert" | "orange" | "attention" | "rouge" | "inconnu";
interface AuditDimension {
  feu: Feu;
  titre: string;
  constat: string;
  sources_appui?: string[];
}
interface AuditReponseAxe {
  axe: string;
  titre: string;
  diagnostic: string;
  sources_appui?: string[];
}
interface AuditOwner { cadre: string; raison: string }
interface AuditAction {
  titre: string;
  detail: string;
  ecueils?: string;
  owner?: AuditOwner;
}
interface AuditAngleMort { titre: string; pourquoi: string }
interface AuditVisibilite {
  sources_mentionnant_marque: number;
  sources_total: number;
  note?: string;
}
interface AuditDiagnostic {
  score_sante: number;
  score_explication?: string;
  reponse_axe: AuditReponseAxe;
  dimensions: {
    perception_marche: AuditDimension;
    concurrence: AuditDimension;
    clarte_message: AuditDimension;
    differenciation: AuditDimension;
    part_de_voix: AuditDimension;
  };
  angles_morts: AuditAngleMort[];
  plan_correctif: {
    quick_wins: AuditAction[];
    chantiers_fond: AuditAction[];
  };
  visibilite_actif: AuditVisibilite;
  metadata?: AnyObj;
}


// Normalise items en groupes { catégorie: SourceItem[] }, en tolérant les deux formats.
function groupSources(
  full: VerdictPayload["research_sources_full"],
  legacy: VerdictPayload["research_sources"],
): { groups: Record<string, SourceItem[]>; total: number; byCat: Record<string, number> } {
  const groups: Record<string, SourceItem[]> = {};
  const items = full?.items;
  if (items && !Array.isArray(items) && typeof items === "object") {
    for (const [cat, arr] of Object.entries(items)) {
      if (Array.isArray(arr) && arr.length) groups[cat] = arr;
    }
  } else {
    const flat: SourceItem[] = Array.isArray(items)
      ? items
      : (legacy?.sample ?? []);
    for (const s of flat) {
      const cat = s.category || "autre";
      (groups[cat] ??= []).push(s);
    }
  }
  const byCat: Record<string, number> = full?.by_category
    ? { ...full.by_category }
    : legacy?.by_category
      ? { ...legacy.by_category }
      : Object.fromEntries(Object.entries(groups).map(([k, v]) => [k, v.length]));
  const computed = Object.values(groups).reduce((n, arr) => n + arr.length, 0);
  const total = full?.total ?? legacy?.total ?? Object.values(byCat).reduce((n, v) => n + v, 0) ?? computed;
  return { groups, total, byCat };
}

const CATEGORY_LABEL: Record<string, string> = {
  article: "Articles",
  press: "Presse",
  linkedin: "LinkedIn",
  video_youtube: "YouTube",
  social_instagram: "Instagram",
  social_tiktok: "TikTok",
  social_x: "X",
  social_pinterest: "Pinterest",
  social_linkedin: "LinkedIn",
  scholar: "Scholar",
  juridique: "Juridique",
};
function catLabel(c: string) {
  return CATEGORY_LABEL[c] ?? c.replace(/_/g, " ");
}

// ─── Tokens cockpit ──────────────────────────────────────────────────────
const C = {
  bg: "#05070A",
  bgGrad: "radial-gradient(circle at 20% 0%, #0E1420 0%, #05070A 60%)",
  panel: "rgba(18, 22, 30, 0.55)",
  panelBorder: "rgba(255,255,255,0.08)",
  panelBorderHi: "rgba(231,255,0,0.35)",
  ink: "#F4F6F8",
  inkSoft: "rgba(244,246,248,0.62)",
  inkMuted: "rgba(244,246,248,0.38)",
  green: "#39FF6A",
  red: "#FF3D5A",
  yellow: "#FFE83D",
  fuchsia: "#FF2EA6",
  cyan: "#5BE8FF",
  serif: '"Fraunces", "Playfair Display", Georgia, serif',
  mono: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
};

const Panel: React.FC<React.PropsWithChildren<{ title?: string; eyebrow?: string; accent?: string; padding?: number; style?: React.CSSProperties }>> = ({ title, eyebrow, accent, padding = 24, style, children }) => (
  <section
    style={{
      background: C.panel,
      border: `1px solid ${C.panelBorder}`,
      borderRadius: 14,
      padding,
      backdropFilter: "blur(16px) saturate(140%)",
      boxShadow: "0 8px 40px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
      position: "relative",
      ...style,
    }}
  >
    {(eyebrow || title) && (
      <header style={{ marginBottom: 18, display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        {eyebrow && (
          <span style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: accent ?? C.inkMuted }}>
            {eyebrow}
          </span>
        )}
        {title && (
          <h2 style={{ fontFamily: C.serif, fontSize: 22, fontWeight: 400, color: C.ink, margin: 0, letterSpacing: "-0.01em" }}>
            {title}
          </h2>
        )}
      </header>
    )}
    {children}
  </section>
);

function isVerif(c: Clarity | null | undefined): c is VerifClarity {
  return !!c && (c as VerifClarity).kind === "verif_observations";
}

// ─── Composant principal ─────────────────────────────────────────────────
function DashboardPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<VerdictPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<VerdictPayload>(`/api/verdict/${id}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setErr("Impossible de charger le verdict."); });
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div style={{ minHeight: "100vh", background: C.bgGrad, color: C.ink, fontFamily: '"Inter", system-ui, sans-serif' }}>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation: none !important; transition: none !important; }
        }
        .hojin-dash a:focus-visible, .hojin-dash button:focus-visible {
          outline: 2px solid ${C.fuchsia}; outline-offset: 3px; border-radius: 4px;
        }
        .hojin-dash ::selection { background: ${C.fuchsia}; color: #000; }
        .hojin-src-chevron svg { transition: transform 320ms cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; }
        details[open] > .hojin-src-summary .hojin-src-chevron svg { transform: rotate(180deg); }
        .hojin-src-hint { opacity: 0.55; transition: opacity 180ms ease, letter-spacing 180ms ease; }
        .hojin-src-summary:hover .hojin-src-hint { opacity: 1; letter-spacing: 1.4px; }
        details[open] > .hojin-src-summary .hojin-src-hint::before { content: "Masquer "; }
        details[open] > .hojin-src-summary .hojin-src-hint { font-size: 0; }
        details[open] > .hojin-src-summary .hojin-src-hint::before { font-size: 10px; }
        @keyframes hojin-src-deploy {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        details[open] > .hojin-src-body { animation: hojin-src-deploy 260ms cubic-bezier(0.4, 0, 0.2, 1); transform-origin: top; }
      `}</style>

      <header style={{ padding: "22px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.panelBorder}`, position: "sticky", top: 0, background: "rgba(5,7,10,0.75)", backdropFilter: "blur(20px)", zIndex: 10 }}>
        <button
          onClick={() => navigate({ to: "/verdict/$id", params: { id } })}
          style={{ background: "transparent", border: `1px solid ${C.panelBorder}`, color: C.ink, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <ArrowLeft size={14} /> Verdict
        </button>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", color: C.inkMuted }}>
          Hōjin · Vue dashboard
        </div>
        <Link
          to="/memo/$id"
          params={{ id }}
          style={{ background: "transparent", border: `1px solid ${C.panelBorder}`, color: C.ink, padding: "8px 14px", borderRadius: 8, fontFamily: C.mono, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <FileText size={14} /> Mémo prose
        </Link>
      </header>

      <main className="hojin-dash" style={{ maxWidth: 1320, margin: "0 auto", padding: "32px 28px 80px" }}>
        {err && (
          <Panel><div style={{ color: C.red, fontFamily: C.mono, fontSize: 13 }}>{err}</div></Panel>
        )}
        {!data && !err && (
          <Panel>
            <div style={{ fontFamily: C.mono, fontSize: 11, color: C.inkMuted, letterSpacing: 2, textTransform: "uppercase" }}>Chargement du cockpit…</div>
          </Panel>
        )}
        {data && (
          isVerif(data.verdict_clarity)
            ? <VerifView data={data} clarity={data.verdict_clarity} />
            : (data.verdict_audit_diagnostic || data.audit_axis)
              ? <PositionnementView data={data} diag={data.verdict_audit_diagnostic ?? null} />
              : <StandardView data={data} clarity={(data.verdict_clarity ?? {}) as StdClarity} />
        )}
      </main>
    </div>
  );
}

// ─── VUE STANDARD ────────────────────────────────────────────────────────
function StandardView({ data, clarity }: { data: VerdictPayload; clarity: StdClarity }) {
  const branch = data.branch ?? "business";
  const score = clarity.verdict?.score;
  const scoreValue = typeof score?.value === "number" ? score.value : null;
  const scoreColor = score?.color_hex || (
    scoreValue === null ? C.inkMuted :
    scoreValue >= 7 ? C.green :
    scoreValue >= 5 ? C.yellow : C.red
  );
  const phrase = clarity.verdict?.phrase || "";
  const summary = clarity.pourquoi?.summary;
  const consensus = clarity.pourquoi?.consensus_points ?? [];
  const recos = clarity.recommandations ?? [];
  const megs = (clarity.mises_en_garde ?? []).slice().sort((a, b) => {
    const order = (s?: string) => s === "critical" ? 0 : s === "warning" ? 1 : 2;
    return order(a.severity) - order(b.severity);
  });
  const plan = clarity.plan_attaque ?? {};
  const ressources = clarity.ressources ?? [];
  const conf = clarity.confidence;
  const confGlobal = clarity.metadata?.score_confiance_global ?? null;
  const drapeaux = clarity.metadata?.drapeaux_attention ?? [];
  const instrument = clarity.instrument;
  const { groups: sourceGroups, total: totalSources, byCat } = groupSources(data.research_sources_full, data.research_sources);
  const acts = data.acts ?? [];

  const title = branch === "audit"
    ? `Positionnement${data.audit_axis ? ` — axe ${data.audit_axis.replace(/_/g, " ")}` : ""}`
    : branch === "tendances" ? "Signal de tendance" : "Saisine business";

  return (
    <div style={{ display: "grid", gap: 24 }}>
      {/* Bandeau titre + jauge */}
      {(() => {
        const showGauge = scoreValue !== null || !!conf?.level || confGlobal !== null;
        return (
          <div style={{ display: "grid", gridTemplateColumns: showGauge ? "minmax(0,1fr) auto" : "minmax(0,1fr)", gap: 24, alignItems: "stretch" }}>
            <Panel eyebrow={title} accent={C.fuchsia} title={phrase || "Verdict du Conseil"} style={{ minWidth: 0 }}>
              {summary && (
                <p style={{ fontFamily: C.serif, fontSize: 17, lineHeight: 1.55, color: C.inkSoft, margin: "0 0 18px" }}>
                  {summary}
                </p>
              )}
              {consensus.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {consensus.map((p, i) => (
                    <li key={i} style={{ fontSize: 14, color: C.ink, display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <CheckCircle2 size={16} style={{ color: C.green, marginTop: 2, flexShrink: 0 }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            {showGauge && <ConvictionGauge value={scoreValue} color={scoreColor} confLevel={conf?.level} confGlobal={confGlobal} />}
          </div>
        );
      })()}

      {/* Instrument tendances */}
      {instrument && instrument.kind === "tendances" && (
        <Panel eyebrow="Signal de tendance" accent={C.cyan} title={`${instrument.classification ?? ""} · ${instrument.reco ?? ""}`}>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: C.inkSoft, margin: 0 }}>{instrument.justification}</p>
        </Panel>
      )}

      {/* Recommandations */}
      {recos.length > 0 && (
        <Panel eyebrow="Recommandations" accent={C.fuchsia} title={`${recos.length} action${recos.length > 1 ? "s" : ""} prioritaire${recos.length > 1 ? "s" : ""}`}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center" }}>
            {recos.map((r, i) => (
              <div key={r.id ?? i} style={{ flex: "1 1 calc(50% - 8px)", minWidth: 280, maxWidth: "calc(50% - 8px)", display: "flex" }}>
                <div style={{ flex: 1 }}><RecoCard reco={r} /></div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Mises en garde */}
      {megs.length > 0 && (
        <Panel eyebrow="Mises en garde" accent={C.red} title="Risques identifiés">
          <div style={{ display: "grid", gap: 12 }}>
            {megs.map((m, i) => <WarningRow key={m.id ?? i} meg={m} />)}
          </div>
        </Panel>
      )}

      {/* Plan d'attaque */}
      {(plan.cette_semaine?.length || plan.ce_mois?.length || plan.mois_prochain?.length) ? (
        <Panel eyebrow="Plan d'attaque" accent={C.yellow} title="Timeline d'exécution">
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            <PlanColumn label="Cette semaine" items={plan.cette_semaine ?? []} accent={C.fuchsia} />
            {(plan.ce_mois?.length ?? 0) > 0 && <PlanColumn label="Ce mois" items={plan.ce_mois ?? []} accent={C.yellow} />}
            {(plan.mois_prochain?.length ?? 0) > 0 && <PlanColumn label="Mois prochain" items={plan.mois_prochain ?? []} accent={C.cyan} />}
          </div>
        </Panel>
      ) : null}

      {/* Ressources */}
      {ressources.length > 0 && (
        <Panel eyebrow="Ressources" accent={C.cyan} title="Outils & références">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
            {ressources.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                 style={{ flex: "1 1 calc(50% - 6px)", minWidth: 260, maxWidth: "calc(50% - 6px)", padding: 14, border: `1px solid ${C.panelBorder}`, borderRadius: 10, background: "rgba(255,255,255,0.02)", textDecoration: "none", color: C.ink, transition: "border-color .2s" }}
                 onMouseEnter={e => e.currentTarget.style.borderColor = C.fuchsia}
                 onMouseLeave={e => e.currentTarget.style.borderColor = C.panelBorder}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.inkMuted }}>{r.type}</span>
                  <ExternalLink size={12} style={{ color: C.inkMuted }} />
                </div>
                <div style={{ fontFamily: C.serif, fontSize: 15, color: C.ink, marginBottom: 4 }}>{r.titre}</div>
                {r.description && <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>{r.description}</div>}
              </a>
            ))}
          </div>
        </Panel>
      )}

      {/* Drapeaux attention */}
      {drapeaux.length > 0 && (
        <Panel eyebrow="Drapeaux d'attention" accent={C.yellow} title="Points de vigilance méthodologique">
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {drapeaux.map((d, i) => (
              <li key={i} style={{ display: "flex", gap: 10, fontSize: 13, color: C.inkSoft, lineHeight: 1.5 }}>
                <AlertTriangle size={14} style={{ color: C.yellow, flexShrink: 0, marginTop: 3 }} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Sources */}
      <SourcesPanel groups={sourceGroups} byCat={byCat} total={totalSources} />


      {/* Débat des cadres */}
      {acts.length > 0 && (
        <Panel eyebrow="Débat des cadres" accent={C.fuchsia} title={`${acts.length} intervention${acts.length > 1 ? "s" : ""}`}>
          <DebateView acts={acts} />
        </Panel>
      )}
    </div>
  );
}

// ─── Sous-composants Standard ────────────────────────────────────────────
function ConvictionGauge({ value, color, confLevel, confGlobal }: { value: number | null; color: string; confLevel?: string; confGlobal: number | null }) {
  const hasScore = value !== null;
  const pct = hasScore ? Math.max(0, Math.min(100, (value as number) * 10)) : 0;
  return (
    <Panel padding={20} style={{ minWidth: 230, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: C.inkMuted, marginBottom: 10 }}>
        Indice de conviction
      </div>
      {hasScore ? (
        <>
          <div style={{ fontFamily: C.serif, fontSize: 64, lineHeight: 1, color, fontWeight: 300, textShadow: `0 0 24px ${color}55` }}>
            {value!.toFixed(1)}
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.inkSoft, marginTop: 2 }}>/ 10</div>
          <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 16, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: color, boxShadow: `0 0 10px ${color}` }} />
          </div>
        </>
      ) : (
        <div style={{ fontFamily: C.serif, fontStyle: "italic", color: C.inkMuted, fontSize: 18, padding: "20px 0" }}>non audité</div>
      )}
      {(confLevel || confGlobal !== null) && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.panelBorder}`, width: "100%", display: "flex", justifyContent: "space-between", fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
          {confLevel && <span style={{ color: confLevel === "verified_high" ? C.green : confLevel === "unverified" ? C.yellow : C.inkSoft }}>{confLevel.replace(/_/g, " ")}</span>}
          {confGlobal !== null && <span style={{ color: C.inkSoft }}>{confGlobal}%</span>}
        </div>
      )}
    </Panel>
  );
}

function PriorityChip({ priority }: { priority?: string }) {
  if (!priority) return null;
  const color = priority === "critical" ? C.red : priority === "high" ? C.yellow : C.cyan;
  return (
    <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, border: `1px solid ${color}55`, color, background: `${color}10` }}>
      {priority}
    </span>
  );
}

function RecoCard({ reco }: { reco: Reco }) {
  return (
    <article style={{ padding: 18, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 10, display: "flex", flexDirection: "column", gap: 12 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <PriorityChip priority={reco.priority} />
        {reco.owner?.cadre && (
          <span style={{ fontFamily: C.serif, fontStyle: "italic", fontSize: 12, color: C.fuchsia }}>
            {reco.owner.cadre}
          </span>
        )}
      </header>
      <h3 style={{ fontFamily: C.serif, fontSize: 17, fontWeight: 400, color: C.ink, margin: 0, lineHeight: 1.3 }}>{reco.titre}</h3>
      {reco.pourquoi && <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.55, margin: 0 }}>{reco.pourquoi}</p>}
      {reco.action_concrete && (
        <div style={{ padding: 10, background: "rgba(255,46,166,0.06)", border: `1px solid ${C.fuchsia}33`, borderRadius: 6, fontSize: 12.5, lineHeight: 1.5, color: C.ink }}>
          <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: C.fuchsia, marginBottom: 4 }}>Action</div>
          {reco.action_concrete}
        </div>
      )}
      {reco.ecueils && (
        <div style={{ fontSize: 12, color: C.inkMuted, fontStyle: "italic", lineHeight: 1.5 }}>
          <strong style={{ color: C.yellow, fontStyle: "normal" }}>Écueil :</strong> {reco.ecueils}
        </div>
      )}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${C.panelBorder}`, fontFamily: C.mono, fontSize: 10, letterSpacing: 1, color: C.inkSoft }}>
        {reco.cout_estime && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Wallet size={11} style={{ color: C.yellow }} />{reco.cout_estime}</span>}
        {reco.temps_estime && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Clock size={11} style={{ color: C.cyan }} />{reco.temps_estime}</span>}
        {reco.lien_plateforme_matching?.url && (
          <a href={reco.lien_plateforme_matching.url} target="_blank" rel="noopener noreferrer"
             style={{ marginLeft: "auto", color: C.fuchsia, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {reco.lien_plateforme_matching.label ?? "Ouvrir"} <ExternalLink size={10} />
          </a>
        )}
      </div>
    </article>
  );
}

function WarningRow({ meg }: { meg: MEG }) {
  const isCrit = meg.severity === "critical";
  const color = isCrit ? C.red : C.yellow;
  const Icon = isCrit ? AlertCircle : AlertTriangle;
  return (
    <div style={{ padding: 14, background: `${color}08`, border: `1px solid ${color}33`, borderRadius: 8, display: "grid", gridTemplateColumns: "auto 1fr", gap: 12 }}>
      <Icon size={18} style={{ color, marginTop: 2 }} />
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <h4 style={{ fontFamily: C.serif, fontSize: 15, color: C.ink, margin: 0, fontWeight: 400 }}>{meg.titre}</h4>
          <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color }}>{meg.severity}</span>
        </div>
        {meg.explication && <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.55, margin: "0 0 8px" }}>{meg.explication}</p>}
        {meg.action_preventive && (
          <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5, paddingLeft: 10, borderLeft: `2px solid ${color}` }}>
            <strong style={{ color, fontFamily: C.mono, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", display: "block", marginBottom: 3 }}>Parade</strong>
            {meg.action_preventive}
          </div>
        )}
      </div>
    </div>
  );
}

function PlanColumn({ label, items, accent }: { label: string; items: PlanItem[]; accent: string }) {
  return (
    <div>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: accent, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${accent}33` }}>
        {label}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ padding: 12, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 8 }}>
            <div style={{ fontFamily: C.serif, fontSize: 14, color: C.ink, lineHeight: 1.35, marginBottom: 8 }}>{it.titre}</div>
            <div style={{ display: "flex", gap: 12, fontFamily: C.mono, fontSize: 10, color: C.inkSoft, letterSpacing: 1 }}>
              {it.cout && <span><Wallet size={10} style={{ color: C.yellow, verticalAlign: "middle", marginRight: 3 }} />{it.cout}</span>}
              {it.temps && <span><Clock size={10} style={{ color: C.cyan, verticalAlign: "middle", marginRight: 3 }} />{it.temps}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcesPanel({
  groups,
  byCat,
  total,
  title = "Recherche documentaire",
  eyebrowPrefix = "Sources",
}: {
  groups: Record<string, SourceItem[]>;
  byCat: Record<string, number>;
  total: number;
  title?: string;
  eyebrowPrefix?: string;
}) {
  const entries = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  if (entries.length === 0) return null;
  return (
    <Panel eyebrow={`${eyebrowPrefix} · ${total}`} accent={C.cyan} title={title}>
      {Object.keys(byCat).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
          {Object.entries(byCat)
            .sort((a, b) => b[1] - a[1])
            .map(([cat, n]) => (
              <span
                key={cat}
                style={{
                  fontFamily: C.mono,
                  fontSize: 10,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: `1px solid ${C.panelBorder}`,
                  color: C.inkSoft,
                }}
              >
                {catLabel(cat)} · {n}
              </span>
            ))}
        </div>
      )}
      <div style={{ display: "grid", gap: 10 }}>
        {entries.map(([cat, items], idx) => (
          <details
            key={cat}
            open={idx === 0}
            style={{
              border: `1px solid ${C.panelBorder}`,
              borderRadius: 8,
              background: "rgba(255,255,255,0.015)",
            }}
          >
            <summary
              className="hojin-src-summary"
              title="Cliquer pour dérouler la liste des sources"
              style={{
                cursor: "pointer",
                listStyle: "none",
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontFamily: C.mono,
                fontSize: 11,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: C.cyan,
                transition: "background 180ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
                <span>{catLabel(cat)}</span>
                <span style={{ color: C.ink, textTransform: "none", letterSpacing: 1 }}>
                  — {items.length} source{items.length > 1 ? "s" : ""}
                </span>
              </span>
              <span
                aria-hidden
                className="hojin-src-chevron"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: C.inkSoft,
                  textTransform: "none",
                  letterSpacing: 1,
                  fontSize: 10,
                }}
              >
                <span className="hojin-src-hint">Voir les sources</span>
                <ChevronDown size={14} />
              </span>
            </summary>
            <div className="hojin-src-body" style={{ display: "grid", gap: 6, padding: "4px 10px 12px", overflow: "hidden" }}>
              {items.map((s, i) => (
                <SourceRow key={i} src={s} />
              ))}
            </div>
          </details>
        ))}
      </div>
    </Panel>
  );
}

function SourceRow({ src }: { src: SourceItem }) {
  const score = src.relevance_score;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 12, padding: "8px 10px", border: `1px solid ${C.panelBorder}`, borderRadius: 6, background: "rgba(255,255,255,0.015)", alignItems: "center" }}>
      <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: C.cyan, minWidth: 64 }}>{src.category ?? "—"}</span>
      <a href={src.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.ink, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {src.title || src.url}
      </a>
      {typeof score === "number" && (
        <span style={{ fontFamily: C.mono, fontSize: 10, color: score >= 0.7 ? C.green : score >= 0.5 ? C.yellow : C.inkMuted }}>
          {Math.round(score * 100)}%
        </span>
      )}
    </div>
  );
}

function DebateView({ acts }: { acts: ActBubble[] }) {
  const rounds = acts.reduce<Record<number, ActBubble[]>>((acc, a) => {
    const r = a.round ?? 1;
    (acc[r] ??= []).push(a);
    return acc;
  }, {});
  return (
    <div style={{ display: "grid", gap: 20 }}>
      {Object.entries(rounds).map(([r, bubbles]) => (
        <div key={r}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: C.fuchsia, marginBottom: 10 }}>
            <Users size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Round {r}
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {bubbles.map((b, i) => (
              <div key={i} style={{ padding: 12, background: b.is_contestation ? `${C.red}08` : "rgba(255,255,255,0.02)", border: `1px solid ${b.is_contestation ? C.red + "33" : C.panelBorder}`, borderRadius: 8 }}>
                <div style={{ fontFamily: C.serif, fontStyle: "italic", fontSize: 13, color: b.is_contestation ? C.red : C.fuchsia, marginBottom: 6 }}>
                  {b.ai_name}{b.is_contestation && " · contestation"}
                </div>
                <p style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.55, margin: 0 }}>{b.content}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── VUE VÉRIF ───────────────────────────────────────────────────────────
function VerifView({ data, clarity }: { data: VerdictPayload; clarity: VerifClarity }) {
  const observations = clarity.observations ?? [];
  const byAngle = observations.reduce<Record<string, Observation[]>>((acc, o) => {
    const k = o.angle || "autre";
    (acc[k] ??= []).push(o);
    return acc;
  }, {});
  const { groups: sourceGroups, total: totalSources, byCat } = groupSources(data.research_sources_full, data.research_sources);

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <Panel eyebrow="Audit documentaire" accent={C.cyan} title={`${observations.length} observation${observations.length > 1 ? "s" : ""} relevée${observations.length > 1 ? "s" : ""}`}>
        <p style={{ fontFamily: C.serif, fontStyle: "italic", color: C.inkSoft, fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Le Conseil n'émet pas de recommandation : il pointe ce qui mérite d'être interrogé, sourcé sur la pièce, et oriente vers le professionnel compétent.
        </p>
      </Panel>

      {clarity.rappel_faits && (
        <Panel eyebrow="Rappel des faits" accent={C.inkMuted}>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: C.inkSoft, margin: 0 }}>{clarity.rappel_faits}</p>
        </Panel>
      )}

      {Object.entries(byAngle).map(([angle, list]) => (
        <Panel key={angle} eyebrow={`Angle · ${angle}`} accent={C.fuchsia} title={`${list.length} point${list.length > 1 ? "s" : ""}`}>
          <div style={{ display: "grid", gap: 14 }}>
            {list.map((o, i) => <ObservationCard key={i} obs={o} />)}
          </div>
        </Panel>
      ))}

      {(clarity.angles_non_couverts?.length ?? 0) > 0 && (
        <Panel eyebrow="Angles non couverts" accent={C.yellow} title="Hors du périmètre du Conseil">
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {clarity.angles_non_couverts!.map((a, i) => (
              <li key={i} style={{ display: "flex", gap: 10, fontSize: 13.5, color: C.inkSoft, lineHeight: 1.55 }}>
                <Target size={14} style={{ color: C.yellow, marginTop: 3, flexShrink: 0 }} />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <SourcesPanel groups={sourceGroups} byCat={byCat} total={totalSources} title="Documentation consultée" eyebrowPrefix="Pièces & sources" />
    </div>
  );
}

function ObservationCard({ obs }: { obs: Observation }) {
  const conv = obs.convergence;
  const convColor = conv === "fort" ? C.green : conv === "moyen" ? C.yellow : C.inkMuted;
  return (
    <article style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 10, display: "grid", gap: 10 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, border: `1px solid ${convColor}55`, color: convColor, background: `${convColor}10` }}>
          convergence {conv}
        </span>
        {obs.pro_a_consulter && (
          <span style={{ fontFamily: C.serif, fontStyle: "italic", fontSize: 12, color: C.fuchsia }}>
            → {obs.pro_a_consulter.replace(/_/g, " ")}
          </span>
        )}
      </header>
      <div style={{ fontFamily: C.serif, fontSize: 15, color: C.ink, lineHeight: 1.45 }}>{obs.constat}</div>
      {obs.question && (
        <div style={{ padding: 10, background: `${C.fuchsia}08`, border: `1px solid ${C.fuchsia}33`, borderRadius: 6, fontSize: 13, lineHeight: 1.5, color: C.ink }}>
          <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: C.fuchsia, marginBottom: 4 }}>
            <Sparkles size={10} style={{ verticalAlign: "middle", marginRight: 4 }} />Question à se poser
          </div>
          {obs.question}
        </div>
      )}
      {obs.droit_general && (
        <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5, fontStyle: "italic" }}>{obs.droit_general}</div>
      )}
      {obs.ancrage && (
        <div style={{ fontFamily: C.mono, fontSize: 10, color: C.inkMuted, letterSpacing: 1, paddingTop: 8, borderTop: `1px solid ${C.panelBorder}` }}>
          ancrage : {obs.ancrage}
        </div>
      )}
    </article>
  );
}

// ─── VUE POSITIONNEMENT (mode Audit — verdict_audit_diagnostic) ──────────
const FEU_COLOR: Record<Feu, string> = {
  vert: C.green,
  orange: C.yellow,
  attention: C.yellow,
  rouge: C.red,
  inconnu: "rgba(244,246,248,0.38)",
};
const FEU_LABEL: Record<Feu, string> = {
  vert: "solide",
  orange: "vigilance",
  attention: "vigilance",
  rouge: "à corriger",
  inconnu: "non évalué",
};
const DIMENSION_LABEL: Record<string, string> = {
  perception_marche: "Perception marché",
  concurrence: "Concurrence",
  clarte_message: "Clarté du message",
  differenciation: "Différenciation",
  part_de_voix: "Part de voix",
};

function PositionnementView({ data, diag }: { data: VerdictPayload; diag: AuditDiagnostic | null }) {
  const { groups: sourceGroups, total: totalSources, byCat } = groupSources(
    data.research_sources_full,
    data.research_sources,
  );
  const loading = !diag;
  const dims: Array<[string, AuditDimension | null]> = diag
    ? [
        ["perception_marche", diag.dimensions.perception_marche],
        ["concurrence", diag.dimensions.concurrence],
        ["clarte_message", diag.dimensions.clarte_message],
        ["differenciation", diag.dimensions.differenciation],
        ["part_de_voix", diag.dimensions.part_de_voix],
      ]
    : [
        ["perception_marche", null],
        ["concurrence", null],
        ["clarte_message", null],
        ["differenciation", null],
        ["part_de_voix", null],
      ];
  const score = diag && typeof diag.score_sante === "number" ? diag.score_sante : null;
  const scoreColor =
    score === null ? C.inkMuted : score >= 7 ? C.green : score >= 5 ? C.yellow : C.red;
  const vis = diag?.visibilite_actif;
  const visRatio = vis && vis.sources_total > 0 ? vis.sources_mentionnant_marque / vis.sources_total : 0;
  const visColor = visRatio >= 0.5 ? C.green : visRatio >= 0.2 ? C.yellow : C.red;
  const axeLabel = diag?.reponse_axe?.axe ?? data.audit_axis ?? "";

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <style>{`
        @keyframes hojinShimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .hojin-skel {
          display: block;
          border-radius: 6px;
          background: linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.04) 100%);
          background-size: 800px 100%;
          animation: hojinShimmer 1.4s linear infinite;
        }
        .hojin-skel--pill { border-radius: 999px; }
      `}</style>

      {/* Bandeau — score de santé */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 24, alignItems: "stretch" }}>
        <Panel eyebrow="Positionnement · Audit stratégique" accent={C.fuchsia} title="Diagnostic de santé du positionnement" style={{ minWidth: 0 }}>
          {loading ? (
            <div style={{ display: "grid", gap: 8 }}>
              <span className="hojin-skel" style={{ height: 14, width: "95%" }} />
              <span className="hojin-skel" style={{ height: 14, width: "88%" }} />
              <span className="hojin-skel" style={{ height: 14, width: "60%" }} />
            </div>
          ) : (
            diag!.score_explication && (
              <p style={{ fontFamily: C.serif, fontSize: 16, lineHeight: 1.6, color: C.inkSoft, margin: 0 }}>
                {diag!.score_explication}
              </p>
            )
          )}
        </Panel>
        <Panel eyebrow="Score de santé" accent={scoreColor} style={{ minWidth: 220, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          {loading ? (
            <>
              <span className="hojin-skel" style={{ height: 56, width: 120 }} />
              <span className="hojin-skel hojin-skel--pill" style={{ height: 6, width: 160, marginTop: 14 }} />
            </>
          ) : score !== null ? (
            <>
              <div style={{ fontFamily: C.serif, fontSize: 64, fontWeight: 300, color: scoreColor, lineHeight: 1, letterSpacing: "-0.03em" }}>
                {score.toFixed(1)}
                <span style={{ fontSize: 22, color: C.inkMuted, marginLeft: 4 }}>/10</span>
              </div>
              <div style={{ marginTop: 14, width: 160, height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ width: `${Math.max(0, Math.min(100, score * 10))}%`, height: "100%", background: scoreColor, borderRadius: 999 }} />
              </div>
            </>
          ) : null}
        </Panel>
      </div>

      {/* Réponse à l'axe prioritaire */}
      <Panel
        eyebrow={`Axe prioritaire${axeLabel ? ` · ${axeLabel.replace(/_/g, " ")}` : ""}`}
        accent={C.fuchsia}
        title={loading ? "Analyse en cours…" : diag!.reponse_axe.titre}
      >
        {loading ? (
          <div style={{ display: "grid", gap: 8 }}>
            <span className="hojin-skel" style={{ height: 13, width: "100%" }} />
            <span className="hojin-skel" style={{ height: 13, width: "97%" }} />
            <span className="hojin-skel" style={{ height: 13, width: "92%" }} />
            <span className="hojin-skel" style={{ height: 13, width: "70%" }} />
          </div>
        ) : (
          <>
            <p style={{ fontFamily: C.serif, fontSize: 15, lineHeight: 1.65, color: C.ink, margin: 0, whiteSpace: "pre-wrap" }}>
              {diag!.reponse_axe.diagnostic}
            </p>
            {diag!.reponse_axe.sources_appui && diag!.reponse_axe.sources_appui.length > 0 && (
              <SourcesAppui urls={diag!.reponse_axe.sources_appui} />
            )}
          </>
        )}
      </Panel>

      {/* 5 dimensions — feux tricolores */}
      <div>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: C.inkMuted, marginBottom: 14 }}>
          Les 5 dimensions du positionnement
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          {dims.map(([key, dim]) => (
            dim
              ? <DimensionCard key={key} label={DIMENSION_LABEL[key] ?? key} dim={dim} />
              : <DimensionCardSkeleton key={key} label={DIMENSION_LABEL[key] ?? key} />
          ))}
        </div>
      </div>

      {/* Angles morts */}
      {loading ? (
        <Panel eyebrow="Angles morts" accent={C.yellow} title="Détection en cours…">
          <div style={{ display: "grid", gap: 10 }}>
            <span className="hojin-skel" style={{ height: 14, width: "85%" }} />
            <span className="hojin-skel" style={{ height: 14, width: "72%" }} />
          </div>
        </Panel>
      ) : diag!.angles_morts && diag!.angles_morts.length > 0 && (
        <Panel eyebrow="Angles morts" accent={C.yellow} title={`${diag!.angles_morts.length} zones aveugles`}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 14 }}>
            {diag!.angles_morts.map((am, i) => (
              <li key={i} style={{ padding: 14, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 8 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
                  <AlertTriangle size={14} style={{ color: C.yellow, marginTop: 3, flexShrink: 0 }} />
                  <div style={{ fontFamily: C.serif, fontSize: 15, color: C.ink, lineHeight: 1.4 }}>{am.titre}</div>
                </div>
                <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.55, margin: "0 0 0 24px" }}>{am.pourquoi}</p>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Plan correctif — 2 colonnes */}
      <Panel eyebrow="Plan correctif" accent={C.green} title="Quick wins & chantiers de fond">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
          {loading ? (
            <>
              <ActionColumnSkeleton label="Quick wins" accent={C.green} />
              <ActionColumnSkeleton label="Chantiers de fond" accent={C.cyan} />
            </>
          ) : (
            <>
              <ActionColumn label="Quick wins" items={diag!.plan_correctif.quick_wins} accent={C.green} />
              <ActionColumn label="Chantiers de fond" items={diag!.plan_correctif.chantiers_fond} accent={C.cyan} />
            </>
          )}
        </div>
      </Panel>

      {/* Visibilité de l'actif */}
      {loading ? (
        <Panel eyebrow="Visibilité de l'actif" accent={C.inkMuted} title="Mesure en cours…">
          <span className="hojin-skel hojin-skel--pill" style={{ height: 8, width: "100%" }} />
        </Panel>
      ) : vis && (
        <Panel eyebrow="Visibilité de l'actif" accent={visColor} title={`${vis.sources_mentionnant_marque}/${vis.sources_total} sources citent la marque`}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${Math.round(visRatio * 100)}%`, height: "100%", background: visColor, borderRadius: 999, minWidth: visRatio > 0 ? 6 : 0 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, color: C.inkMuted }}>
              <span>0</span>
              <span>{Math.round(visRatio * 100)}% de part de voix</span>
              <span>{vis.sources_total}</span>
            </div>
          </div>
          {vis.note && (
            <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.6, margin: 0 }}>{vis.note}</p>
          )}
        </Panel>
      )}

      {/* Sources — panneau standard (toujours affiché dès que disponible) */}
      <SourcesPanel groups={sourceGroups} byCat={byCat} total={totalSources} />
    </div>
  );
}

function DimensionCardSkeleton({ label }: { label: string }) {
  return (
    <article style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderLeft: `3px solid ${C.inkMuted}`, borderRadius: 10, display: "grid", gap: 10 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.inkMuted }}>
          {label}
        </span>
        <span className="hojin-skel hojin-skel--pill" style={{ height: 18, width: 76 }} />
      </header>
      <span className="hojin-skel" style={{ height: 14, width: "80%" }} />
      <span className="hojin-skel" style={{ height: 11, width: "100%" }} />
      <span className="hojin-skel" style={{ height: 11, width: "70%" }} />
    </article>
  );
}

function ActionColumnSkeleton({ label, accent }: { label: string; accent: string }) {
  return (
    <div>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: accent, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${accent}33` }}>
        {label}
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ padding: 14, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 8, display: "grid", gap: 8 }}>
            <span className="hojin-skel" style={{ height: 14, width: "75%" }} />
            <span className="hojin-skel" style={{ height: 11, width: "100%" }} />
            <span className="hojin-skel" style={{ height: 11, width: "60%" }} />
          </div>
        ))}
      </div>
    </div>
  );
}


function DimensionCard({ label, dim }: { label: string; dim: AuditDimension }) {
  const color = FEU_COLOR[dim.feu] ?? C.inkMuted;
  return (
    <article style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderLeft: `3px solid ${color}`, borderRadius: 10, display: "grid", gap: 10 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.inkMuted }}>
          {label}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: C.mono, fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, border: `1px solid ${color}55`, color, background: `${color}12` }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: dim.feu !== "inconnu" ? `0 0 8px ${color}88` : "none" }} />
          {FEU_LABEL[dim.feu] ?? dim.feu}
        </span>
      </header>
      <div style={{ fontFamily: C.serif, fontSize: 14, color: C.ink, lineHeight: 1.4 }}>{dim.titre}</div>
      <p style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.55, margin: 0 }}>{dim.constat}</p>
      {dim.sources_appui && dim.sources_appui.length > 0 && <SourcesAppui urls={dim.sources_appui} compact />}
    </article>
  );
}

function ActionColumn({ label, items, accent }: { label: string; items: AuditAction[]; accent: string }) {
  return (
    <div>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: accent, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${accent}33` }}>
        {label}
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {items.map((a, i) => (
          <div key={i} style={{ padding: 14, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.panelBorder}`, borderRadius: 8, display: "grid", gap: 8 }}>
            <div style={{ fontFamily: C.serif, fontSize: 14.5, color: C.ink, lineHeight: 1.4 }}>{a.titre}</div>
            <p style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.55, margin: 0 }}>{a.detail}</p>
            {a.ecueils && (
              <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5, paddingLeft: 10, borderLeft: `2px solid ${C.red}55` }}>
                <strong style={{ color: C.red, fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", display: "block", marginBottom: 3 }}>Écueils</strong>
                {a.ecueils}
              </div>
            )}
            {a.owner && (
              <div style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "baseline", gap: 6, fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, color: C.inkMuted, paddingTop: 4, borderTop: `1px solid ${C.panelBorder}`, marginTop: 2 }}>
                <span style={{ textTransform: "uppercase" }}>Pris en charge par</span>
                <span style={{ color: C.fuchsia, fontFamily: C.serif, fontStyle: "italic", fontSize: 13, letterSpacing: 0 }}>{a.owner.cadre}</span>
                <span style={{ color: C.inkSoft, textTransform: "none", letterSpacing: 0, fontFamily: C.serif, fontStyle: "italic", fontSize: 12 }}>— {a.owner.raison}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcesAppui({ urls, compact }: { urls: string[]; compact?: boolean }) {
  if (!urls || urls.length === 0) return null;
  return (
    <div style={{ marginTop: compact ? 6 : 12, display: "grid", gap: 4, paddingTop: compact ? 6 : 10, borderTop: `1px solid ${C.panelBorder}` }}>
      <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: C.inkMuted, marginBottom: 2 }}>
        Sources d'appui · {urls.length}
      </div>
      {urls.map((u, i) => (
        <a
          key={i}
          href={u}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.cyan, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={u}
        >
          <ExternalLink size={10} style={{ flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{u.replace(/^https?:\/\/(www\.)?/, "")}</span>
        </a>
      ))}
    </div>
  );
}

export default DashboardPage;
